import React from 'react';

export const DEFAULT_CANVAS_CONTENT_SETTLE_DELAY_MS = 90;

function normalizeText(value) {
  return String(value ?? '').trim();
}

function fingerprint(value) {
  const text = normalizeText(value);
  return `${text.length}:${text}`;
}

function canvasTabKeys(tabs) {
  return (Array.isArray(tabs) ? tabs : [])
    .map((tab) => normalizeText(tab?.key))
    .filter(Boolean)
    .join(',');
}

export function buildCanvasContentSignature({
  activeTabKey,
  analysisContent,
  analysisPending,
  contentGenerating,
  intakeContent,
  showCanvasPanel,
  tabs,
} = {}) {
  if (!showCanvasPanel) {
    return 'closed';
  }
  const tabKey = normalizeText(activeTabKey);
  const tabKeys = canvasTabKeys(tabs);
  if (tabKey === 'analysis') {
    return `analysis:${analysisPending ? 'pending' : 'ready'}:${contentGenerating ? 'generating' : 'idle'}:${fingerprint(analysisContent)}:${tabKeys}`;
  }
  if (tabKey === 'requirement') {
    return `requirement:${contentGenerating ? 'generating' : 'idle'}:${fingerprint(intakeContent)}:${tabKeys}`;
  }
  return `tab:${tabKey}:${tabKeys}`;
}

export function resolveCommittedCanvasContentAvailable({
  activeTabKey = '',
  analysisContent = '',
  analysisTabKey = 'analysis',
  intakeContent = '',
  requirementTabKey = 'requirement',
} = {}) {
  const tabKey = normalizeText(activeTabKey);
  const contentByTab = {
    [analysisTabKey]: analysisContent,
    [requirementTabKey]: intakeContent,
  };
  return Boolean(normalizeText(contentByTab[tabKey]));
}

export function shouldSuspendCanvasContent({
  activeTabKey,
  analysisTabKey = 'analysis',
  committedContentAvailable,
  requirementTabKey = 'requirement',
  showCanvasPanel,
  signature,
} = {}) {
  if (!showCanvasPanel || !signature) {
    return false;
  }
  const tabKey = normalizeText(activeTabKey);
  if (committedContentAvailable && [analysisTabKey, requirementTabKey].includes(tabKey)) {
    return false;
  }
  return [analysisTabKey, requirementTabKey].includes(tabKey);
}

function contentSettlingOptions(options) {
  const defaults = {
    activeTabKey: '',
    analysisContent: '',
    analysisPending: false,
    analysisTabKey: 'analysis',
    contentGenerating: false,
    intakeContent: '',
    requirementTabKey: 'requirement',
    settleDelayMs: DEFAULT_CANVAS_CONTENT_SETTLE_DELAY_MS,
    showCanvasPanel: false,
    tabs: [],
  };
  return {
    ...options,
    ...Object.fromEntries(Object.entries(defaults).map(([key, fallback]) => [
      key, options[key] === undefined ? fallback : options[key],
    ])),
  };
}

export function useCanvasContentSettling(options = {}) {
  const {
    activeTabKey,
    analysisContent,
    analysisPending,
    analysisTabKey,
    contentGenerating,
    intakeContent,
    requirementTabKey,
    settleDelayMs,
    showCanvasPanel,
    tabs,
  } = contentSettlingOptions(options);
  const signature = React.useMemo(() => buildCanvasContentSignature({
    activeTabKey,
    analysisContent,
    analysisPending,
    contentGenerating,
    intakeContent,
    showCanvasPanel,
    tabs,
  }), [
    activeTabKey,
    analysisContent,
    analysisPending,
    contentGenerating,
    intakeContent,
    showCanvasPanel,
    tabs,
  ]);
  const committedContentAvailable = resolveCommittedCanvasContentAvailable({
    activeTabKey,
    analysisContent,
    analysisTabKey,
    intakeContent,
    requirementTabKey,
  });
  const shouldSuspend = shouldSuspendCanvasContent({
    activeTabKey,
    analysisTabKey,
    committedContentAvailable,
    requirementTabKey,
    showCanvasPanel,
    signature,
  });
  const [settledSignature, setSettledSignature] = React.useState('');

  React.useEffect(() => {
    if (!shouldSuspend) {
      setSettledSignature(signature);
      return undefined;
    }
    const timer = setTimeout(() => {
      setSettledSignature(signature);
    }, settleDelayMs);
    return () => clearTimeout(timer);
  }, [settleDelayMs, shouldSuspend, signature]);

  return shouldSuspend && (contentGenerating || settledSignature !== signature);
}

export default useCanvasContentSettling;
